import { logger } from './logger';

export interface ErrorReport {
  id: string;
  timestamp: Date;
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
  userId?: string;
  component?: string;
  errorBoundary?: boolean;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'ui' | 'api' | 'auth' | 'data' | 'network' | 'unknown';
  metadata?: Record<string, any>;
  sessionId: string;
  buildVersion?: string;
}

export interface ErrorStats {
  totalErrors: number;
  errorsByCategory: Record<string, number>;
  errorsBySeverity: Record<string, number>;
  mostCommonErrors: Array<{
    message: string;
    count: number;
    lastOccurrence: Date;
  }>;
  errorTrend: Array<{
    date: string;
    count: number;
  }>;
}

/**
 * 錯誤監控和分析系統
 */
export class ErrorMonitoring {
  private static instance: ErrorMonitoring;
  private errors: ErrorReport[] = [];
  private sessionId: string;
  private maxStoredErrors = 100; // 最大儲存的錯誤數量
  private reportingEnabled = false;
  private buildVersion: string;

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.buildVersion = process.env.REACT_APP_VERSION || 'unknown';
    this.reportingEnabled = process.env.NODE_ENV === 'production';
    
    // 監聽未捕獲的錯誤
    this.setupGlobalErrorHandlers();
  }

  static getInstance(): ErrorMonitoring {
    if (!ErrorMonitoring.instance) {
      ErrorMonitoring.instance = new ErrorMonitoring();
    }
    return ErrorMonitoring.instance;
  }

  /**
   * 記錄錯誤
   */
  reportError(
    error: Error,
    context?: {
      component?: string;
      userId?: string;
      severity?: ErrorReport['severity'];
      category?: ErrorReport['category'];
      metadata?: Record<string, any>;
      errorBoundary?: boolean;
    }
  ): void {
    const errorReport: ErrorReport = {
      id: this.generateErrorId(),
      timestamp: new Date(),
      message: error.message,
      stack: error.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
      buildVersion: this.buildVersion,
      severity: context?.severity || this.categorizeErrorSeverity(error),
      category: context?.category || this.categorizeError(error),
      ...context
    };

    // 添加到本地儲存
    this.addErrorToStorage(errorReport);

    // 記錄到控制台
    logger.error('ERROR_MONITORING', `Error reported: ${error.message}`, {
      errorId: errorReport.id,
      severity: errorReport.severity,
      category: errorReport.category
    });

    // 在生產環境中上報錯誤
    if (this.reportingEnabled) {
      this.sendErrorReport(errorReport);
    }

    // 對於嚴重錯誤，立即處理
    if (errorReport.severity === 'critical') {
      this.handleCriticalError(errorReport);
    }
  }

  /**
   * 獲取錯誤統計
   */
  getErrorStats(): ErrorStats {
    const now = new Date();
    const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    // 過濾最近7天的錯誤
    const recentErrors = this.errors.filter(error => error.timestamp >= last7Days);

    // 按類別統計
    const errorsByCategory: Record<string, number> = {};
    const errorsBySeverity: Record<string, number> = {};
    const errorMessageCounts: Record<string, { count: number; lastOccurrence: Date }> = {};

    recentErrors.forEach(error => {
      // 類別統計
      errorsByCategory[error.category] = (errorsByCategory[error.category] || 0) + 1;
      
      // 嚴重程度統計
      errorsBySeverity[error.severity] = (errorsBySeverity[error.severity] || 0) + 1;
      
      // 錯誤訊息統計
      const key = error.message;
      if (errorMessageCounts[key]) {
        errorMessageCounts[key].count++;
        if (error.timestamp > errorMessageCounts[key].lastOccurrence) {
          errorMessageCounts[key].lastOccurrence = error.timestamp;
        }
      } else {
        errorMessageCounts[key] = { count: 1, lastOccurrence: error.timestamp };
      }
    });

    // 最常見的錯誤
    const mostCommonErrors = Object.entries(errorMessageCounts)
      .map(([message, data]) => ({ message, ...data }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // 錯誤趋势（按天統計）
    const errorTrend = this.generateErrorTrend(recentErrors);

    return {
      totalErrors: recentErrors.length,
      errorsByCategory,
      errorsBySeverity,
      mostCommonErrors,
      errorTrend
    };
  }

  /**
   * 清除舊錯誤
   */
  clearOldErrors(): void {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    this.errors = this.errors.filter(error => error.timestamp >= thirtyDaysAgo);
    
    logger.info('ERROR_MONITORING', 'Cleared old errors', {
      remainingErrors: this.errors.length
    });
  }

  /**
   * 匯出錯誤報告
   */
  exportErrorReport(): string {
    const stats = this.getErrorStats();
    const report = {
      generatedAt: new Date().toISOString(),
      sessionId: this.sessionId,
      buildVersion: this.buildVersion,
      stats,
      recentErrors: this.errors.slice(-20) // 最近20個錯誤
    };

    return JSON.stringify(report, null, 2);
  }

  /**
   * 設定全域錯誤處理器
   */
  private setupGlobalErrorHandlers(): void {
    // 未捕獲的 JavaScript 錯誤
    window.addEventListener('error', (event) => {
      this.reportError(new Error(event.message), {
        category: 'unknown',
        severity: 'high',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          globalError: true
        }
      });
    });

    // 未處理的 Promise 拒絕
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason instanceof Error 
        ? event.reason 
        : new Error(String(event.reason));
        
      this.reportError(error, {
        category: 'unknown',
        severity: 'high',
        metadata: {
          unhandledPromise: true,
          reason: event.reason
        }
      });
    });
  }

  /**
   * 將錯誤添加到儲存
   */
  private addErrorToStorage(errorReport: ErrorReport): void {
    this.errors.push(errorReport);
    
    // 限制儲存的錯誤數量
    if (this.errors.length > this.maxStoredErrors) {
      this.errors = this.errors.slice(-this.maxStoredErrors);
    }
  }

  /**
   * 分類錯誤嚴重程度
   */
  private categorizeErrorSeverity(error: Error): ErrorReport['severity'] {
    const message = error.message.toLowerCase();
    
    if (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('timeout')
    ) {
      return 'medium';
    }
    
    if (
      message.includes('permission') ||
      message.includes('unauthorized') ||
      message.includes('auth')
    ) {
      return 'high';
    }
    
    if (
      message.includes('cannot read') ||
      message.includes('undefined') ||
      message.includes('null')
    ) {
      return 'high';
    }
    
    return 'medium';
  }

  /**
   * 分類錯誤類型
   */
  private categorizeError(error: Error): ErrorReport['category'] {
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';
    
    if (message.includes('auth') || message.includes('login') || message.includes('token')) {
      return 'auth';
    }
    
    if (message.includes('network') || message.includes('fetch') || message.includes('cors')) {
      return 'network';
    }
    
    if (message.includes('firestore') || message.includes('database') || message.includes('firebase')) {
      return 'data';
    }
    
    if (stack.includes('api') || message.includes('api')) {
      return 'api';
    }
    
    if (stack.includes('component') || message.includes('render')) {
      return 'ui';
    }
    
    return 'unknown';
  }

  /**
   * 生成錯誤趨勢
   */
  private generateErrorTrend(errors: ErrorReport[]): Array<{ date: string; count: number }> {
    const last7Days = [];
    const now = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dateStr = date.toISOString().split('T')[0];
      
      const count = errors.filter(error => {
        const errorDate = error.timestamp.toISOString().split('T')[0];
        return errorDate === dateStr;
      }).length;
      
      last7Days.push({ date: dateStr, count });
    }
    
    return last7Days;
  }

  /**
   * 處理嚴重錯誤
   */
  private handleCriticalError(errorReport: ErrorReport): void {
    logger.error('CRITICAL_ERROR', 'Critical error detected', errorReport);
    
    // 可以在這裡添加更多的處理邏輯，例如：
    // - 自動重新載入頁面
    // - 發送緊急通知
    // - 清除可能損壞的快取
  }

  /**
   * 發送錯誤報告到外部服務
   */
  private async sendErrorReport(errorReport: ErrorReport): Promise<void> {
    try {
      // 這裡可以整合第三方錯誤監控服務，例如 Sentry
      // 暫時使用 console.log 代替實際的發送
      console.log('📊 Error report would be sent to monitoring service:', {
        id: errorReport.id,
        message: errorReport.message,
        category: errorReport.category,
        severity: errorReport.severity
      });
      
      // 實際實作時可以取消註解下面的程式碼：
      /*
      await fetch('/api/errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorReport)
      });
      */
    } catch (error) {
      logger.warn('ERROR_MONITORING', 'Failed to send error report', error);
    }
  }

  /**
   * 生成 Session ID
   */
  private generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 生成錯誤 ID
   */
  private generateErrorId(): string {
    return `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// 匯出單例實例
export const errorMonitoring = ErrorMonitoring.getInstance();
